import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Play, AlertCircle, CheckCircle, Moon, Clock, Timer, Ban, AlertTriangle } from 'lucide-react';

// Types for modal state
export interface CreateNodeModalState {
  isOpen: boolean;
}

export interface BranchModalState {
  nodeId: string;
  nodeName: string;
}

export interface ConnectModalState {
  fromNodeId: string;
  fromNodeName: string;
}

export interface StatusModalState {
  nodeId: string;
  nodeName: string;
  currentStatus: string;
}

interface NodeData {
  id: string;
  name: string;
  status: string;
}

interface EdgeData {
  from: string;
  to: string;
}

export interface StreamGraphModalsProps {
  // Create Node Modal
  showCreateNode: boolean;
  newNodeName: string;
  isCreatingNode: boolean;
  onCreateNodeClose: () => void;
  onNewNodeNameChange: (name: string) => void;
  onCreateNode: () => void;

  // Branch Modal
  showBranchModal: BranchModalState | null;
  branchName: string;
  isBranching: boolean;
  onBranchModalClose: () => void;
  onBranchNameChange: (name: string) => void;
  onCreateBranch: () => void;

  // Connect Modal
  showConnectModal: ConnectModalState | null;
  nodes: NodeData[];
  edges: EdgeData[];
  isCreatingEdge: boolean;
  onConnectModalClose: () => void;
  onCreateEdge: (toNodeId: string) => void;

  // Status Modal
  showStatusModal: StatusModalState | null;
  isUpdatingStatus: boolean;
  onStatusModalClose: () => void;
  onUpdateStatus: (newStatus: string) => void;
}

const STATUS_OPTIONS = [
  { status: 'pending', label: 'Pending', icon: Clock, color: 'text-text-muted' },
  { status: 'ready', label: 'Ready', icon: Play, color: 'text-primary-400' },
  { status: 'running', label: 'Running', icon: Loader2, color: 'text-primary-400' },
  { status: 'waiting', label: 'Waiting', icon: Timer, color: 'text-amber-400' },
  { status: 'attention', label: 'Attention', icon: AlertCircle, color: 'text-warning-400' },
  { status: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-success-400' },
  { status: 'sleeping', label: 'Sleeping', icon: Moon, color: 'text-purple-400' },
  { status: 'cancelled', label: 'Cancelled', icon: Ban, color: 'text-surface-400' },
  { status: 'failed', label: 'Failed', icon: AlertTriangle, color: 'text-red-400' },
] as const;

export function StreamGraphModals({
  // Create Node Modal props
  showCreateNode,
  newNodeName,
  isCreatingNode,
  onCreateNodeClose,
  onNewNodeNameChange,
  onCreateNode,
  // Branch Modal props
  showBranchModal,
  branchName,
  isBranching,
  onBranchModalClose,
  onBranchNameChange,
  onCreateBranch,
  // Connect Modal props
  showConnectModal,
  nodes,
  edges,
  isCreatingEdge,
  onConnectModalClose,
  onCreateEdge,
  // Status Modal props
  showStatusModal,
  isUpdatingStatus,
  onStatusModalClose,
  onUpdateStatus,
}: StreamGraphModalsProps) {
  return (
    <>
      {/* Create Node Modal */}
      <AnimatePresence>
        {showCreateNode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 flex items-center justify-center z-30"
            onClick={onCreateNodeClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-surface-800 border border-surface-600 rounded-lg p-4 w-80 shadow-xl"
            >
              <h3 className="text-lg font-semibold mb-3">Add New Task</h3>
              <input
                type="text"
                value={newNodeName}
                onChange={(e) => onNewNodeNameChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onCreateNode()}
                placeholder="Task name..."
                className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary-500 mb-3"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={onCreateNodeClose}
                  className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onCreateNode}
                  disabled={!newNodeName.trim() || isCreatingNode}
                  className="px-3 py-1.5 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isCreatingNode ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Create'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Branch Modal */}
      <AnimatePresence>
        {showBranchModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 flex items-center justify-center z-30"
            onClick={onBranchModalClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-surface-800 border border-surface-600 rounded-lg p-4 w-80 shadow-xl"
            >
              <h3 className="text-lg font-semibold mb-1">Create Branch</h3>
              <p className="text-sm text-text-muted mb-3">
                Create a parallel task from "{showBranchModal.nodeName}"
              </p>
              <input
                type="text"
                value={branchName}
                onChange={(e) => onBranchNameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && branchName.trim()) {
                    onCreateBranch();
                  }
                }}
                placeholder="Branch name..."
                className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500 mb-3"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={onBranchModalClose}
                  className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onCreateBranch}
                  disabled={!branchName.trim() || isBranching}
                  className="px-3 py-1.5 bg-primary-500 hover:bg-primary-400 disabled:bg-surface-600 disabled:text-text-muted rounded-lg text-sm text-surface-900 transition-colors flex items-center gap-2"
                >
                  {isBranching && <Loader2 className="w-3 h-3 animate-spin" />}
                  Create Branch
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connect Modal */}
      <AnimatePresence>
        {showConnectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 flex items-center justify-center z-30"
            onClick={onConnectModalClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-surface-800 border border-surface-600 rounded-lg p-4 w-80 shadow-xl max-h-96 overflow-hidden flex flex-col"
            >
              <h3 className="text-lg font-semibold mb-1">Connect to Task</h3>
              <p className="text-sm text-text-muted mb-3">
                Create edge from "{showConnectModal.fromNodeName}" to:
              </p>
              <div className="flex-1 overflow-y-auto space-y-1 mb-3">
                {nodes.filter(n => {
                  // Exclude self and already connected nodes
                  if (n.id === showConnectModal.fromNodeId) return false;
                  const alreadyConnected = edges.some(
                    e => e.from === showConnectModal.fromNodeId && e.to === n.id
                  );
                  return !alreadyConnected;
                }).map(node => (
                  <button
                    key={node.id}
                    onClick={() => onCreateEdge(node.id)}
                    disabled={isCreatingEdge}
                    className="w-full px-3 py-2 text-left text-sm bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-between"
                  >
                    <span>{node.name}</span>
                    <span className="text-xs text-text-muted capitalize">{node.status}</span>
                  </button>
                ))}
                {nodes.filter(n => n.id !== showConnectModal.fromNodeId && !edges.some(e => e.from === showConnectModal.fromNodeId && e.to === n.id)).length === 0 && (
                  <p className="text-sm text-text-muted text-center py-4">No available tasks to connect</p>
                )}
              </div>
              <button
                onClick={onConnectModalClose}
                className="w-full px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Change Modal */}
      <AnimatePresence>
        {showStatusModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 flex items-center justify-center z-30"
            onClick={onStatusModalClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-surface-800 border border-surface-600 rounded-lg p-4 w-72 shadow-xl"
            >
              <h3 className="text-lg font-semibold mb-1">Change Status</h3>
              <p className="text-sm text-text-muted mb-3">
                "{showStatusModal.nodeName}"
              </p>
              <div className="space-y-1">
                {STATUS_OPTIONS.map(({ status, label, icon: Icon, color }) => (
                  <button
                    key={status}
                    onClick={() => onUpdateStatus(status)}
                    disabled={isUpdatingStatus || showStatusModal.currentStatus === status}
                    className={`w-full px-3 py-2 text-left text-sm rounded-lg transition-colors flex items-center gap-2 ${
                      showStatusModal.currentStatus === status
                        ? 'bg-surface-600 text-text-primary'
                        : 'bg-surface-700 hover:bg-surface-600 text-text-secondary hover:text-text-primary'
                    } disabled:opacity-50`}
                  >
                    <Icon className={`w-4 h-4 ${color}`} />
                    <span>{label}</span>
                    {showStatusModal.currentStatus === status && (
                      <span className="ml-auto text-xs text-text-muted">Current</span>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={onStatusModalClose}
                className="w-full mt-3 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
